import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsCard from './components/StatsCard';
import RevenueChart from './components/RevenueChart';
import OrdersTable from './components/OrdersTable';
import ProductManagement from './components/ProductManagement';
import InventoryAlerts from './components/InventoryAlerts';
import CustomerAnalytics from './components/CustomerAnalytics';
import QuickActions from './components/QuickActions';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [notifications, setNotifications] = useState([
    { id: 1, type: 'order', message: 'New order received', time: '2 min ago' },
    { id: 2, type: 'stock', message: 'Low stock alert: AirPods Pro', time: '15 min ago' },
    { id: 3, type: 'customer', message: 'New customer support ticket', time: '1 hour ago' }
  ]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'BarChart3' },
    { id: 'orders', name: 'Orders', icon: 'Package' },
    { id: 'products', name: 'Products', icon: 'Grid3X3' },
    { id: 'inventory', name: 'Inventory', icon: 'AlertTriangle' },
    { id: 'customers', name: 'Customers', icon: 'Users' },
    { id: 'analytics', name: 'Analytics', icon: 'TrendingUp' }
  ];

  const statsData = [
    {
      title: 'Total Revenue',
      value: 'DZ 10,590,750',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'DollarSign',
      color: 'success'
    },
    {
      title: 'Total Orders',
      value: '1,234',
      change: '+8.2%',
      changeType: 'positive',
      icon: 'Package',
      color: 'primary'
    },
    {
      title: 'Active Products',
      value: '456',
      change: '+3.1%',
      changeType: 'positive',
      icon: 'Grid3X3',
      color: 'primary'
    },
    {
      title: 'Total Customers',
      value: '2,890',
      change: '+15.3%',
      changeType: 'positive',
      icon: 'Users',
      color: 'success'
    },
    {
      title: 'Conversion Rate',
      value: '3.2%',
      change: '-0.5%',
      changeType: 'negative',
      icon: 'TrendingUp',
      color: 'warning'
    },
    {
      title: 'Avg Order Value',
      value: 'DZ 12,083',
      change: '+5.7%',
      changeType: 'positive',
      icon: 'CreditCard',
      color: 'success'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {statsData?.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <StatsCard {...stat} />
                </motion.div>
              ))}
            </div>
            {/* Quick Actions */}
            <QuickActions />
            {/* Revenue Chart */}
            <div className="holographic-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-foreground">Revenue Analytics</h3>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm" iconName="Calendar">
                    This Month
                  </Button>
                  <Button variant="outline" size="sm" iconName="Download">
                    Export
                  </Button>
                </div>
              </div>
              <RevenueChart type="bar" />
            </div>
            {/* Recent Orders Preview */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="holographic-card rounded-xl p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">Recent Orders</h3>
                <div className="space-y-3">
                  {[1, 2, 3]?.map((_, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/10 rounded-lg">
                      <div>
                        <p className="font-medium text-foreground">Order #ORD-2024-00{index + 1}</p>
                        <p className="text-sm text-muted-foreground">Customer Name</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-foreground">{`DZ ${Math.round((Math.random() * 1000 + 100) * 135).toLocaleString()}`}</p>
                        <span className="text-xs px-2 py-1 bg-success/20 text-success rounded-full">
                          Completed
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" fullWidth className="mt-4">
                  View All Orders
                </Button>
              </div>

              <div className="holographic-card rounded-xl p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">Low Stock Alerts</h3>
                <div className="space-y-3">
                  {[
                    { name: 'AirPods Pro 2nd Gen', stock: 0, status: 'critical' },
                    { name: 'Wireless Mouse Pro', stock: 3, status: 'warning' },
                    { name: 'MacBook Pro 16"', stock: 12, status: 'warning' }
                  ]?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/10 rounded-lg">
                      <div>
                        <p className="font-medium text-foreground">{item?.name}</p>
                        <p className="text-sm text-muted-foreground">Stock: {item?.stock} units</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item?.status === 'critical' ?'bg-error/20 text-error' :'bg-warning/20 text-warning'
                      }`}>
                        {item?.status === 'critical' ? 'Critical' : 'Low Stock'}
                      </span>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" fullWidth className="mt-4">
                  View All Alerts
                </Button>
              </div>
            </div>
          </div>
        );
      case 'orders':
        return <OrdersTable />;
      case 'products':
        return <ProductManagement />;
      case 'inventory':
        return <InventoryAlerts />;
      case 'customers':
        return <CustomerAnalytics />;
      case 'analytics':
        return (
          <div className="space-y-6">
            <div className="holographic-card rounded-xl p-6">
              <h3 className="text-xl font-bold text-foreground mb-6">Revenue Trends</h3>
              <RevenueChart type="line" />
            </div>
            <CustomerAnalytics />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Header */}
      <div className="bg-card/50 backdrop-blur-sm border-b border-border sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg trust-seal">
                <Icon name="Shield" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold command-text bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Admin Command Center
                </h1>
                <p className="text-sm text-muted-foreground font-mono">
                  {currentTime?.toLocaleDateString()} • {currentTime?.toLocaleTimeString()}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Return to Home */}
              <Button variant="outline" size="sm" iconName="Home" onClick={() => navigate('/') }>
                Return Home
              </Button>

              {/* Notifications */}
              <div className="relative">
                <Button variant="ghost" size="icon" className="relative">
                  <Icon name="Bell" size={20} />
                  {notifications?.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center">
                      {notifications?.length}
                    </span>
                  )}
                </Button>
              </div>

              {/* System Status */}
              <div className="flex items-center space-x-2 px-3 py-2 bg-success/10 border border-success/30 rounded-lg">
                <div className="inventory-pulse high"></div>
                <span className="text-sm text-success font-medium">System Online</span>
              </div>

              {/* Admin Profile */}
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">Admin User</p>
                  <p className="text-xs text-muted-foreground">Super Admin</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex items-center space-x-1 mb-8 overflow-x-auto scrollbar-hide">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`neural-nav-item flex items-center space-x-2 px-4 py-3 rounded-lg whitespace-nowrap transition-all duration-300 ${
                activeTab === tab?.id
                  ? 'bg-primary/20 text-primary border border-primary/30' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span className="font-medium">{tab?.name}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderTabContent()}
        </motion.div>
      </div>
      {/* Data Stream Animation */}
      <div className="data-stream"></div>
    </div>
  );
};

export default AdminDashboard;